import posthog from 'posthog-js'

// ── PostHog wrapper ──────────────────────────────────────────────────────────
//
// Primary purpose: error/exception tracking and performance (web vitals).
// Product-analytics events are opt-in, manually fired, and only ever start
// after the user has accepted cookies via ConsentContext — nothing here
// runs before that.
//
// Deliberately OFF: autocapture (click/form capture), heatmaps, session
// replay. Deliberately ON: exception capture, web vitals. Everything else
// is fired manually via analytics.capture().

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined

let initialized = false

export const analytics = {
  /** Call only after consent is granted. Safe to call more than once. */
  init(): void {
    if (initialized) return
    if (!POSTHOG_KEY || !POSTHOG_HOST) {
      console.warn('[analytics] Missing VITE_POSTHOG_KEY / VITE_POSTHOG_HOST — skipping init')
      return
    }

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,

      // Only create a profile once we identify() a signed-in user —
      // avoids building profiles on anonymous/pre-consent traffic.
      person_profiles: 'identified_only',

      // No automatic click/form/input capture.
      autocapture: false,

      // SPA: we fire pageviews ourselves on route change.
      capture_pageview: false,
      capture_pageleave: false,

      // No heatmaps, no session replay.
      enable_heatmaps: false,
      disable_session_recording: true,

      // Web vitals (LCP/FID/CLS/FCP) — performance telemetry, not behavioral.
      capture_performance: true,

      // Exception/error capture — this is the primary reason PostHog is here.
      capture_exceptions: true,
    })

    initialized = true
  },

  /** Fully tears down the client and clears local PostHog storage (on reject/withdraw). */
  shutdown(): void {
    if (!initialized) return
    posthog.reset()
    initialized = false
  },

  isInitialized(): boolean {
    return initialized
  },

  capture(event: string, properties?: Record<string, unknown>): void {
    if (!initialized) return
    posthog.capture(event, properties)
  },

  pageview(path?: string): void {
    if (!initialized) return
    posthog.capture('$pageview', path ? { $current_url: path } : undefined)
  },

  identify(userId: string, properties?: Record<string, unknown>): void {
    if (!initialized) return
    posthog.identify(userId, properties)
  },

  /**
   * Manually report a handled error — anything that stopped the user from
   * completing an action, even if the UI recovered gracefully (a toast, an
   * inline error state, etc). Not for expected/routine flows (e.g. a normal
   * session-expiry redirect to login) and not for warnings.
   * `context` is arbitrary extra properties (endpoint, action, componentStack…)
   * attached to the $exception event to make it debuggable.
   */
  captureException(error: unknown, context?: Record<string, unknown>): void {
    if (!initialized) return
    posthog.captureException(error, context)
  },

  /** Call on logout — keeps the anonymous device id but drops the person link. */
  resetIdentity(): void {
    if (!initialized) return
    posthog.reset()
  },
}
